import { resolveEscapeRoomMediaUrl } from "../components/escape-room/erRendererStyleUtils";
import { getElementsForViewport } from "../components/escape-room/erViewport";
import { isRegistrationPage } from "./escapeRoomConstants";

const prefetchedUrls = new Set();
const prefetchedQuizIds = new Set();

function prefetchUrl(url) {
	const resolved = resolveEscapeRoomMediaUrl(url);
	if (!resolved || prefetchedUrls.has(resolved)) return;
	prefetchedUrls.add(resolved);
	const lower = resolved.toLowerCase();
	if (/\.(mp4|webm|ogg|mov)(\?|$)/i.test(lower)) {
		const v = document.createElement("video");
		v.preload = "auto";
		v.muted = true;
		v.playsInline = true;
		v.src = resolved;
		try {
			v.load();
		} catch (_) {
			/* noop */
		}
		return;
	}
	const img = new Image();
	img.decoding = "async";
	img.src = resolved;
}

function walkElementsForMedia(elements, onUrl) {
	if (!Array.isArray(elements)) return;
	for (const el of elements) {
		if (!el || typeof el !== "object") continue;
		const c = el.content || {};
		if (el.type === "image" || el.type === "video") {
			onUrl(c.url);
		}
		if (el.type === "shape" && c.fillImageUrl) onUrl(c.fillImageUrl);
		if (el.type === "button" && c.modalDesign?.elements?.length) {
			walkElementsForMedia(c.modalDesign.elements, onUrl);
		}
		if (el.type === "hint") {
			for (const key of ["hintModalDesign", "confirmModalDesign"]) {
				if (c[key]?.elements?.length) walkElementsForMedia(c[key].elements, onUrl);
			}
		}
	}
}

function resolveQuizId(raw) {
	if (!raw) return null;
	if (typeof raw === "string") return raw.trim() || null;
	if (typeof raw === "object" && raw._id) return String(raw._id);
	return null;
}

function walkElementsForQuizzes(elements, onQuizId) {
	if (!Array.isArray(elements)) return;
	for (const el of elements) {
		if (!el || typeof el !== "object") continue;
		if (el.type === "quiz") {
			const qid = resolveQuizId(el.content?.quizId);
			if (qid) onQuizId(qid);
		}
		const c = el.content || {};
		if (el.type === "button" && c.modalDesign?.elements?.length) {
			walkElementsForQuizzes(c.modalDesign.elements, onQuizId);
		}
		if (el.type === "hint") {
			for (const key of ["hintModalDesign", "confirmModalDesign"]) {
				if (c[key]?.elements?.length) walkElementsForQuizzes(c[key].elements, onQuizId);
			}
		}
	}
}

/**
 * Precarga fondos, medios de elementos y quizzes de una página ER (idempotente).
 */
export function prefetchEscapeRoomPage(page, { fetchQuiz } = {}) {
	if (!page) return;
	if (page.backgroundImageUrl) prefetchUrl(page.backgroundImageUrl);
	if (isRegistrationPage(page) && page.registrationDesign?.backgroundImageUrl) {
		prefetchUrl(page.registrationDesign.backgroundImageUrl);
	}
	if (page.registrationDesign?.logoUrl) prefetchUrl(page.registrationDesign.logoUrl);

	const viewports = ["desktop", "tablet", "mobile"];
	const seenEls = new Set();
	for (const vp of viewports) {
		const els = getElementsForViewport(page, vp) || [];
		for (const el of els) {
			const key = el?.elementId || JSON.stringify(el);
			if (seenEls.has(key)) continue;
			seenEls.add(key);
			walkElementsForMedia([el], prefetchUrl);
			if (typeof fetchQuiz === "function") {
				walkElementsForQuizzes([el], (qid) => {
					if (!prefetchedQuizIds.has(qid)) {
						prefetchedQuizIds.add(qid);
						Promise.resolve(fetchQuiz(qid)).catch(() => {});
					}
				});
			}
		}
	}
}
